/** @jsxImportSource @emotion/react */
import { Table } from "../../../components/table-components/Table";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { PageFooter } from "../../../components/page-skeleton/PageFooter";
import { StyledText } from "../../../assets/typography/typography";
import { TableRow } from "../../../components/table-components/TableRow";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { css } from "@emotion/react";
import { colors } from "../../../components/colors";
import { Fragment, MutableRefObject, useState } from "react";
import Sortable from "sortablejs";
import { DragAndDropContainer } from "../../../components/DragAndDropContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../error-handler";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { SpinnerBlock } from "../../common/Spinner";
import { KitchenCategory } from "../../kitchen-categories/models/kitchen-category";
import { MutatePackingItemDrawer } from "./MutatePackingItemDrawer";
import { TableEllipsisDropdown } from "../../../components/TableEllipsisDropdown";
import { PackingItem } from "../models/packing-item";
import {
  useDeletePackingItem,
  useUpdatePackingItemSorting,
} from "../service/packing-items-api";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { useSearchParamsManipulation } from "../../../hooks/use-searchparams-manipulation";

type Props = {
  packingItems: PackingItem[];
  kitchenCategories: KitchenCategory[];
};

const sortTableCellStyle = css`
  cursor: grab;
`;

const dragAndDropContainerStyle = css`
  border-bottom: 1px solid ${colors.greyBorder};
`;

export const PackingItemsTable = ({
  packingItems,
  kitchenCategories,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { option, setOption, removeOption } = useSearchParamsManipulation();
  const [packingItemToEditId, setPackingItemToEditId] = useState<string>(
    option || ""
  );

  const {
    mutateAsync: updateSorting,
    isLoading: isUpdatingSorting,
  } = useUpdatePackingItemSorting();

  const {
    mutateAsync: deletePackingItem,
    isLoading: isDeletingPackingItem,
  } = useDeletePackingItem();

  const onDrop = async (
    event: Sortable.SortableEvent,
    containerRef: MutableRefObject<HTMLElement | null>
  ) => {
    if (!containerRef) {
      return;
    }
    // iterating over dom children. Each of them has the id of the category as data attribute
    // @ts-expect-error
    const categories = [...containerRef.current?.children].map(
      (b) => b.dataset.id
    );
    try {
      await updateSorting({
        data: {
          sort: categories,
        },
        organizationKeyName,
      });
      toastr.success("Success", "Packing items reordered successfully");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  if (isUpdatingSorting) {
    return <SpinnerBlock />;
  }

  return (
    <div>
      <Table>
        <thead>
          <TableRowHeader>
            <TableCellHeaderText>Sorting</TableCellHeaderText>
            <TableCellHeaderText>Admin name</TableCellHeaderText>
            <TableCellHeaderText>
              Display name (visible on receipt)
            </TableCellHeaderText>
            <TableCellHeaderText>Description</TableCellHeaderText>
            <TableCellHeaderText>Receipt setting</TableCellHeaderText>
            <TableCellHeaderText>Kitchen category</TableCellHeaderText>
            <TableCellHeaderText>Unit</TableCellHeaderText>
            <th />
          </TableRowHeader>
        </thead>
        <DragAndDropContainer
          isTable={true}
          onEnd={onDrop}
          containerStyle={dragAndDropContainerStyle}
        >
          {packingItems
            ?.sort((a, b) => a.sortKey - b.sortKey)
            .map((ing, i) => {
              return (
                <Fragment key={i}>
                  <TableRow data-id={ing.id}>
                    <TableCellText className="pl-5" css={sortTableCellStyle}>
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        <FontAwesomeIcon icon={solid("grip-vertical")} />
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(ing.id);
                        setPackingItemToEditId(ing.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {ing.name}
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(ing.id);
                        setPackingItemToEditId(ing.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {ing.title}
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(ing.id);
                        setPackingItemToEditId(ing.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {ing.description || "-"}
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(ing.id);
                        setPackingItemToEditId(ing.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {ing.receiptSetting}
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(ing.id);
                        setPackingItemToEditId(ing.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {kitchenCategories.find(
                          (cat) => cat.id === ing.kitchenCategoryId
                        )?.name || "-"}
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(ing.id);
                        setPackingItemToEditId(ing.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {ing.unit || "-"}
                      </StyledText>
                    </TableCellText>
                    <TableEllipsisDropdown
                      menu={{
                        items: [
                          {
                            key: "1",
                            label: (
                              <DropdownItemButton
                                onClick={() => {
                                  setOption(ing.id);
                                  setPackingItemToEditId(ing.id);
                                }}
                                type="button"
                                icon={<FontAwesomeIcon icon={solid("pen")} />}
                              >
                                Edit packing item
                              </DropdownItemButton>
                            ),
                          },
                          {
                            key: "2",
                            label: (
                              <DropdownItemButton
                                onClick={async () => {
                                  try {
                                    await deletePackingItem({
                                      data: { id: ing.id },
                                      organizationKeyName,
                                    });
                                    toastr.success(
                                      "Success",
                                      "Packing item deleted"
                                    );
                                  } catch (error: unknown) {
                                    // Getting status 500. Most cases it's because of the product that is using this packing item
                                    const message = getErrorMessage(error);
                                    toastr.error("Error", message);
                                  }
                                }}
                                type="button"
                                icon={
                                  isDeletingPackingItem ? (
                                    <ActionSpinner />
                                  ) : (
                                    <FontAwesomeIcon icon={solid("trash")} />
                                  )
                                }
                                disabled={isDeletingPackingItem}
                              >
                                Delete packing item
                              </DropdownItemButton>
                            ),
                          },
                        ],
                      }}
                    />
                  </TableRow>
                </Fragment>
              );
            })}
        </DragAndDropContainer>
      </Table>
      <MutatePackingItemDrawer
        isOpen={!!packingItemToEditId}
        onClose={() => {
          removeOption();
          setPackingItemToEditId("");
        }}
        id={packingItemToEditId}
      />
      <PageFooter>
        <StyledText className="bodyTextRegular mb-0">
          {kitchenCategories?.length}{" "}
          {kitchenCategories?.length === 1 ? "packing item" : "packing items"}{" "}
          in total
        </StyledText>
      </PageFooter>
    </div>
  );
};
